// ─────────────────────────────────────────────────────────────────────────────
// Entry point — imports Woodland CSS, handles Okta PKCE callback, mounts App.
// No CDN references — all assets are locally bundled.
//
// The Okta PKCE flow redirects to the app root with ?code=&state= query params.
// We must call handleLoginRedirect() before React mounts so the router never
// sees the raw callback URL and the hash-route is restored correctly.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react'
import ReactDOM from 'react-dom/client'
import './styles/colors_and_type.css'
import './styles/app.css'
import { loadRuntimeConfig } from './lib/runtimeConfig'
import { readLoginError } from './lib/loginError'
import { SignInError } from './components/app-shell/SignInError'

async function main() {
  // authStore and the API client read their settings at module load, so they
  // are imported only once the runtime config is in.
  await loadRuntimeConfig()
  const { oktaEnabled, oktaAuth } = await import('./lib/authStore')
  const { default: App } = await import('./App')

  const root = document.getElementById('root')
  if (!root) throw new Error('Missing #root element in index.html')

  // Okta came back with ?error= (e.g. access_denied: not assigned to the app).
  // Render the error instead of the app: AuthGuard would send them back to
  // Okta, which still has their session and returns the same error -- a loop.
  const loginError = oktaEnabled
    ? readLoginError(window.location.search, window.location.hash)
    : null
  if (loginError) {
    ReactDOM.createRoot(root).render(
      <React.StrictMode>
        <SignInError error={loginError} />
      </React.StrictMode>,
    )
    return
  }

  // If Okta is configured and this page load is a redirect back from Okta
  // (URL contains ?code=&state=), exchange the auth code for tokens before
  // rendering. This clears the code from the URL so the router sees a clean URL.
  if (oktaEnabled && oktaAuth?.isLoginRedirect()) {
    try {
      await oktaAuth.handleLoginRedirect()
    } catch (err) {
      // If the callback fails (e.g. replayed code), log and continue — the
      // AuthGuard will re-trigger login if the user still has no session.
      console.error('[Okta] handleLoginRedirect failed:', err)
    }
  }

  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  )
}

void main()
